import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import {
  KnowledgebaseForm,
  type KnowledgebaseFormValues,
} from "@/components/admin/intake/knowledgebase-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  BookOpen,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { KnowledgebaseEntry } from "@db/schema";

interface KnowledgebaseResponse {
  data: KnowledgebaseEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "website_change", label: "Website Change" },
  { value: "tech_support", label: "Tech Support" },
  { value: "creative_design", label: "Creative Design" },
  { value: "new_project", label: "New Project" },
  { value: "other", label: "Other" },
] as const;

const REVIEW_STATUSES = [
  { value: "all", label: "All Review Statuses" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  website_change: "Website Change",
  tech_support: "Tech Support",
  creative_design: "Creative Design",
  new_project: "New Project",
  other: "Other",
};

function keywordsToString(keywords: string[] | null | undefined): string {
  if (!keywords || keywords.length === 0) return "";
  return keywords.join(", ");
}

function stringToKeywords(str: string): string[] {
  return str
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function KnowledgebasePage() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [reviewStatusFilter, setReviewStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgebaseEntry | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<KnowledgebaseEntry | null>(
    null,
  );

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "20");
  if (search.trim()) queryParams.set("search", search.trim());
  if (categoryFilter !== "all") queryParams.set("category", categoryFilter);
  if (reviewStatusFilter !== "all")
    queryParams.set("reviewStatus", reviewStatusFilter);

  const queryKeyUrl = `/api/intake/knowledgebase?${queryParams.toString()}`;

  const { data, isLoading } = useQuery<KnowledgebaseResponse>({
    queryKey: [queryKeyUrl],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: async (formData: KnowledgebaseFormValues) => {
      const payload = {
        title: formData.title,
        keywords: stringToKeywords(formData.keywords),
        category: formData.category,
        estimatedMinutes: formData.estimatedMinutes,
        helpDocUrl: formData.helpDocUrl || null,
        solutionNotes: formData.solutionNotes || null,
        isActive: formData.isActive,
        reviewStatus: formData.reviewStatus,
      };
      const res = await apiRequest("POST", "/api/intake/knowledgebase", payload);
      return (await res.json()) as KnowledgebaseEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0] as string).startsWith("/api/intake/knowledgebase"),
      });
      setDialogOpen(false);
      toast({ title: "Knowledgebase entry created" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to create entry",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      formData,
    }: {
      id: string;
      formData: KnowledgebaseFormValues;
    }) => {
      const payload = {
        title: formData.title,
        keywords: stringToKeywords(formData.keywords),
        category: formData.category,
        estimatedMinutes: formData.estimatedMinutes,
        helpDocUrl: formData.helpDocUrl || null,
        solutionNotes: formData.solutionNotes || null,
        isActive: formData.isActive,
        reviewStatus: formData.reviewStatus,
      };
      const res = await apiRequest(
        "PATCH",
        `/api/intake/knowledgebase/${id}`,
        payload,
      );
      return (await res.json()) as KnowledgebaseEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0] as string).startsWith("/api/intake/knowledgebase"),
      });
      setDialogOpen(false);
      setEditingEntry(null);
      toast({ title: "Knowledgebase entry updated" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update entry",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/intake/knowledgebase/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0] as string).startsWith("/api/intake/knowledgebase"),
      });
      setDeleteTarget(null);
      toast({ title: "Knowledgebase entry deleted" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to delete entry",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function handleOpenCreate() {
    setEditingEntry(null);
    setDialogOpen(true);
  }

  function handleOpenEdit(entry: KnowledgebaseEntry) {
    setEditingEntry(entry);
    setDialogOpen(true);
  }

  function handleFormSubmit(formData: KnowledgebaseFormValues) {
    if (editingEntry) {
      updateMutation.mutate({ id: editingEntry.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  }

  const columns: ColumnDef<KnowledgebaseEntry>[] = [
    {
      key: "title",
      header: "Title",
      cell: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: "category",
      header: "Category",
      cell: (row) => (
        <span className="text-sm">
          {CATEGORY_LABELS[row.category] ?? row.category}
        </span>
      ),
      className: "w-[140px]",
    },
    {
      key: "estimatedMinutes",
      header: "Est. Time",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.estimatedMinutes}m
        </span>
      ),
      className: "w-[100px]",
    },
    {
      key: "isActive",
      header: "Status",
      cell: (row) => (
        <Badge
          variant="outline"
          className={
            row.isActive
              ? "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20"
              : "bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20"
          }
        >
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
      className: "w-[100px]",
    },
    {
      key: "reviewStatus",
      header: "Review Status",
      cell: (row) => (
        <StatusBadge type="kbReview" status={row.reviewStatus} />
      ),
      className: "w-[140px]",
    },
    {
      key: "actions",
      header: "",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEdit(row);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      className: "w-[50px]",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Knowledgebase"
        description="Manage knowledgebase entries used for AI scoping"
        action={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Entry
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={data?.data}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search by title or keywords..."
        emptyIcon={BookOpen}
        emptyTitle="No knowledgebase entries"
        emptyDescription="Create knowledgebase entries to improve AI scoping accuracy."
        emptyAction={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Entry
          </Button>
        }
        pagination={
          data?.pagination
            ? {
                page: data.pagination.page,
                totalPages: data.pagination.totalPages,
                onPageChange: setPage,
              }
            : undefined
        }
        filterSlot={
          <div className="flex gap-2">
            <Select
              value={categoryFilter}
              onValueChange={(val) => {
                setCategoryFilter(val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={reviewStatusFilter}
              onValueChange={(val) => {
                setReviewStatusFilter(val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Review Status" />
              </SelectTrigger>
              <SelectContent>
                {REVIEW_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingEntry(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? "Edit Entry" : "Add Knowledgebase Entry"}
            </DialogTitle>
            <DialogDescription>
              {editingEntry
                ? "Update the knowledgebase entry below."
                : "Create a new knowledgebase entry for AI scoping reference."}
            </DialogDescription>
          </DialogHeader>
          <KnowledgebaseForm
            key={editingEntry?.id ?? "new"}
            defaultValues={
              editingEntry
                ? {
                    title: editingEntry.title,
                    keywords: keywordsToString(editingEntry.keywords),
                    category: editingEntry.category,
                    estimatedMinutes: editingEntry.estimatedMinutes,
                    helpDocUrl: editingEntry.helpDocUrl ?? "",
                    solutionNotes: editingEntry.solutionNotes ?? "",
                    isActive: editingEntry.isActive,
                    reviewStatus: editingEntry.reviewStatus,
                  }
                : undefined
            }
            onSubmit={handleFormSubmit}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Knowledgebase Entry"
        description={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() =>
          deleteTarget && deleteMutation.mutate(deleteTarget.id)
        }
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
